import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Calendar, Users, HeartHandshake, Sparkles, ArrowRight, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { MemberQuickNav } from '../MemberQuickNav';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';

const WelcomePage = () => {
  const { user } = useAuth();
  const { c } = useContent();

  if (!user) return null;

  const steps = [
    {
      icon: BadgeCheck,
      title: c('welcome.step1.title', 'Save Your Member ID'),
      text: c('welcome.step1.text', "Your member ID is shown above and on your dashboard — you'll use it at meetings and events."),
      link: null,
    },
    {
      icon: Calendar,
      title: c('welcome.step2.title', 'RSVP to Our Next Meeting'),
      text: c('welcome.step2.text', 'Our monthly membership meetings are where it all happens. Reserve your seat for the next one.'),
      link: { to: '/events', label: 'See Upcoming Events' },
    },
    {
      icon: HeartHandshake,
      title: c('welcome.step3.title', 'Join a Committee'),
      text: c('welcome.step3.text', "Committees are where the club's work gets planned. Pick the one that fits your interests and talents."),
      link: { to: '/get-involved#volunteer', label: 'Browse Committees' },
    },
    {
      icon: Users,
      title: c('welcome.step4.title', 'Meet Your Fellow Members'),
      text: c('welcome.step4.text', 'Browse the member directory from your dashboard and introduce yourself — every founding friendship starts with hello.'),
      link: { to: '/member-dashboard', label: 'Open the Directory' },
    },
    {
      icon: Sparkles,
      title: c('welcome.step5.title', 'Mark the Kickoff'),
      text: c('welcome.step5.text', 'Our Fall 2026 recruitment cycle kicks off at the September 1 Membership Meeting at 6:00 PM. Be there as history starts.'),
      link: { to: '/founding-members', label: 'Founding Member Details' },
    },
  ];

  return (
    <main className="min-h-screen pt-52 bg-[#f1e3eb] pb-20" data-testid="welcome-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <MemberQuickNav />
        {/* Hero */}
        <div className="text-center mb-12">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
            Founding Member Welcome Packet
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6" data-testid="welcome-heading">
            {c('welcome.hero.title', `Welcome to the Club, ${user.first_name}!`)}
          </h1>
          <p className="text-lg text-[#3c4a2a]/80 leading-relaxed max-w-2xl mx-auto">
            {c('welcome.hero.text', "You're officially one of the women building The Women's Club of Lincoln County from the ground up. Here's everything you need to get started as a founding member.")}
          </p>
          {user.member_id && (
            <div className="mt-8 inline-flex items-center gap-3 bg-[#5d6b3c] text-white rounded-full px-6 py-3" data-testid="welcome-member-id">
              <BadgeCheck className="w-5 h-5" />
              <span className="text-sm uppercase tracking-wider text-white/80">Member ID</span>
              <span className="font-mono font-medium">{user.member_id}</span>
            </div>
          )}
        </div>

        {/* Steps */}
        <div className="space-y-5">
          {steps.map((step, i) => (
            <Card key={step.title} className="bg-white border-0 shadow-md" data-testid={`welcome-step-${i + 1}`}>
              <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-[#5d6b3c] text-white flex items-center justify-center font-serif text-lg">
                    {i + 1}
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#bd8cae]/25 flex items-center justify-center">
                    <step.icon className="w-6 h-6 text-[#5d6b3c]" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-xl text-[#3c4a2a] mb-1">{step.title}</h3>
                  <p className="text-[#3c4a2a]/70 text-sm leading-relaxed">{step.text}</p>
                </div>
                {step.link && (
                  <Link to={step.link.to} className="flex-shrink-0">
                    <Button
                      variant="outline"
                      className="border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white whitespace-nowrap"
                      data-testid={`welcome-step-${i + 1}-btn`}
                    >
                      {step.link.label}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Questions + Dashboard CTA */}
        <div className="mt-12 text-center bg-white rounded-2xl shadow-md p-8">
          <Mail className="w-8 h-8 text-[#bd8cae] mx-auto mb-3" />
          <p className="text-[#3c4a2a]/80 mb-6 max-w-xl mx-auto">
            {c('welcome.questions.text', "Questions? Email us anytime at Info@wclcnc.com — we're so glad you're here.")}
          </p>
          <Link to="/member-dashboard">
            <Button className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-8 py-6 text-lg" data-testid="welcome-dashboard-btn">
              Go to My Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </main>
  );
};

export default WelcomePage;
